import { Game } from 'boardgame.io';
import type { DetroitGameState } from '../types';
import { setupDetroitGame } from './setupDetroitBankruptcySolo';
import { issueSharesPhase } from './phases/issueSharesPhase';
import { selectActionsDetroitPhase } from './phases/selectActionsDetroitPhase';
import { buildTrackDetroitPhase } from './phases/buildTrackDetroitPhase';
import { moveGoodsPhase } from './phases/moveGoodsPhase';
import { collectIncomePhase } from './phases/collectIncomePhase';
import { payExpensesDetroitPhase } from './phases/payExpensesDetroitPhase';
import { incomeReductionDetroitPhase } from './phases/incomeReductionDetroitPhase';
import { refillGoodsDetroitPhase } from './phases/refillGoodsDetroitPhase';
import { advanceTurnDetroitPhase } from './phases/advanceTurnDetroitPhase';
import { wrapPhaseMoves } from './utils/moveLogger';

/**
 * Age of Steam - Detroit Bankruptcy Solo ゲーム定義（Boardgame.io）
 *
 * Detroit Bankruptcy Solo固有ルールに従ったソロプレイ専用ゲーム実装。
 *
 * 特徴:
 * - ターン制限なし（破産まで継続）
 * - 勝敗条件: ターン7以上で破産 = 勝利、ターン6以下 = 敗北
 * - 3種類のアクション（Engineer, Locomotive, Urbanization）各3回まで
 * - 株式発行上限25枚、初期状態: 株式5枚発行済み・現金0
 *
 * 実装済み: Phase 1, 3-10
 * 未実装: Phase 2（プレイ順決定、ソロプレイではスキップ）
 *
 * @see docs/specifications/logic/detroit_bankruptcy_solo/*.md
 * @see data/map/detroit_bankruptcy_solo/detroit_bankruptcy_solo_rules.md
 */
export const DetroitBankruptcySoloGame: Game<DetroitGameState> = {
  name: 'age-of-steam-detroit-bankruptcy-solo',

  // デフォルトのターン動作
  turn: {},

  setup: ({ ctx }: { ctx: any }) => {
    // Boardgame.io v0.50では、setup関数の第1引数は { ctx, ... } の形式
    return setupDetroitGame(ctx);
  },

  // プレイヤー人数の範囲（Detroit Bankruptcy Solo: 固定1人）
  minPlayers: 1,
  maxPlayers: 1,

  phases: wrapPhaseMoves({
    // Phase 1: 株式発行（共通Phase、maxSharesLimit: 25 で上限設定）
    issueShares: issueSharesPhase as any,

    // Phase 2: プレイ順決定はソロプレイではスキップ

    // Phase 3: アクション選択（Detroit固有: 3種類×3回ディスク制）
    selectActions: selectActionsDetroitPhase,

    // Phase 4: 線路建設（Detroit専用: エンジニアボーナス適用）
    buildTrack: buildTrackDetroitPhase,

    // Phase 5: 商品輸送（共通Phase）
    moveGoods: moveGoodsPhase as any,

    // Phase 6: 収入獲得（自動処理Phase、共通）
    collectIncome: collectIncomePhase as any,

    // Phase 7: 経費支払い（Detroit固有: ターン数コスト追加）
    payExpenses: payExpensesDetroitPhase,

    // Phase 8: 収入減少（Detroit固有: 5ステップ制）
    incomeReduction: incomeReductionDetroitPhase,

    // Phase 9: 商品補充（Detroit専用: 両側ダイスロール）
    refillGoods: refillGoodsDetroitPhase,

    // Phase 10: ターン進行（Detroit固有: ターン制限なし）
    advanceTurn: advanceTurnDetroitPhase
  }),

  // ゲーム終了条件
  endIf: ({ G }: { G: DetroitGameState }) => {
    if (G.isGameOver) {
      // Detroit Bankruptcy Solo: 勝敗はisVictoriousで判定
      const soloPlayer = G.players['0'];
      return {
        winner: soloPlayer.isVictorious ? '0' : undefined
      };
    }
  }
};
